import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Description,
  PictureAsPdf,
  Image,
  AttachFile,
} from '@mui/icons-material';
import { DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from '../../services/leads';

const DocumentUpload = ({ 
  open, 
  onClose, 
  onUpload, 
  leadId, 
  loading = false,
  existingDocuments = [] 
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setError('');

    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`File ${file.name} has invalid type. Allowed: PDF, JPG, JPEG, PNG, GIF.`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!documentType) {
      setError('Please select a document type');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const desc = description || `${DOCUMENT_TYPE_LABELS[documentType]} - ${file.name}`;

        setUploadProgress(prev => ({ ...prev, [i]: 0 }));

        // Use the new service method signature
        const response = await onUpload(leadId, file, documentType, desc, (progress) => {
          setUploadProgress(prev => ({ ...prev, [i]: progress }));
        });

        // Enhanced response handling
        if (response.error) {
          throw new Error(response.error);
        }
      }

      // Reset form
      setSelectedFiles([]);
      setDocumentType('');
      setDescription('');
      setUploadProgress({});
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.file?.[0] ||
                          error.message ||
                          'Failed to upload documents. Please try again.';
      setError(errorMessage);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') return <PictureAsPdf color="error" />;
    if (fileType.startsWith('image/')) return <Image color="primary" />;
    return <AttachFile color="action" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getExistingDocumentsByType = () => {
    if (!Array.isArray(existingDocuments)) {
      return {};
    }
    return existingDocuments.reduce((acc, doc) => {
      if (!acc[doc.document_type]) {
        acc[doc.document_type] = [];
      }
      acc[doc.document_type].push(doc);
      return acc;
    }, {});
  };

  const existingByType = getExistingDocumentsByType();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        Upload Documents
        {leadId && (
          <Typography variant="caption" display="block" color="text.secondary">
            Lead ID: {leadId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* File Upload Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Upload New Documents
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Document Type *</InputLabel>
              <Select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                label="Document Type *"
              >
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description (Optional)"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Add a description for the documents..."
            />

            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />

            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
              sx={{ mb: 2 }}
            >
              Select Files
            </Button>

            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
              Allowed: PDF, JPG, JPEG, PNG, GIF • Max size: 10MB per file
            </Typography>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({selectedFiles.length})
                </Typography>
                {selectedFiles.map((file, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          {getFileIcon(file.type)}
                          <Box>
                            <Typography variant="body2" noWrap>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleRemoveFile(index)}
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      {uploadProgress[index] !== undefined && (
                        <LinearProgress 
                          variant="determinate" 
                          value={uploadProgress[index]} 
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Grid>

          {/* Existing Documents Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Existing Documents
            </Typography>

            {!Array.isArray(existingDocuments) || existingDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No documents uploaded yet.
              </Typography>
            ) : (
              <Box>
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => {
                  const docs = existingByType[type] || [];
                  if (docs.length === 0) return null;

                  return (
                    <Box key={type} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {label}
                        <Chip 
                          label={docs.length} 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      {docs.map((doc) => (
                        <Card key={doc.id} variant="outlined" sx={{ mb: 1 }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Description fontSize="small" />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" noWrap>
                                  {doc.file_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {doc.description}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={loading || selectedFiles.length === 0 || !documentType}
          startIcon={<CloudUpload />}
        >
          {loading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUpload;